import { getPreset } from './registry';
import type { ColorRamp, ThemeTokens } from './tokens';

/**
 * Every token as a CSS custom property (`--cf-<group>-<name>`), for places MUI
 * doesn't reach: plain CSS, inline styles on bare elements, chart containers.
 * The browser recomputes anything referencing them when the preset changes.
 */
export const tokensToCssVars = (t: ThemeTokens): Record<string, string> => {
  const vars: Record<string, string> = {};

  const ramp = (group: string, r: ColorRamp) => {
    vars[`--cf-${group}-lighter`] = r.lighter;
    vars[`--cf-${group}-light`] = r.light;
    vars[`--cf-${group}`] = r.main;
    vars[`--cf-${group}-dark`] = r.dark;
    vars[`--cf-${group}-darker`] = r.darker;
    vars[`--cf-${group}-contrast`] = r.contrastText;
  };

  ramp('primary', t.brand.primary);
  ramp('secondary', t.brand.secondary);
  ramp('accent', t.brand.accent);

  ramp('info', t.status.info);
  ramp('success', t.status.success);
  ramp('warning', t.status.warning);
  ramp('error', t.status.error);
  ramp('neutral-status', t.status.neutral);

  (Object.entries(t.neutral) as [string, string][]).forEach(([k, v]) => {
    vars[`--cf-${k}`] = v;
  });

  vars['--cf-canvas'] = t.surface.canvas;
  vars['--cf-paper'] = t.surface.paper;
  vars['--cf-raised'] = t.surface.raised;
  vars['--cf-sunken'] = t.surface.sunken;
  vars['--cf-scrim'] = t.surface.scrim;

  vars['--cf-text'] = t.text.primary;
  vars['--cf-text-secondary'] = t.text.secondary;
  vars['--cf-text-disabled'] = t.text.disabled;
  vars['--cf-text-inverse'] = t.text.inverse;
  vars['--cf-link'] = t.text.link;

  vars['--cf-border-subtle'] = t.border.subtle;
  vars['--cf-border'] = t.border.default;
  vars['--cf-border-strong'] = t.border.strong;
  vars['--cf-border-focus'] = t.border.focus;

  vars['--cf-hover'] = t.state.hover;
  vars['--cf-selected'] = t.state.selected;
  vars['--cf-selected-hover'] = t.state.selectedHover;
  vars['--cf-pressed'] = t.state.pressed;
  vars['--cf-disabled-bg'] = t.state.disabledBg;

  vars['--cf-elev-0'] = t.elevation.e0;
  vars['--cf-elev-1'] = t.elevation.e1;
  vars['--cf-elev-2'] = t.elevation.e2;
  vars['--cf-elev-3'] = t.elevation.e3;
  vars['--cf-elev-4'] = t.elevation.e4;

  vars['--cf-radius-sm'] = `${t.radius.sm}px`;
  vars['--cf-radius'] = `${t.radius.md}px`;
  vars['--cf-radius-lg'] = `${t.radius.lg}px`;
  vars['--cf-radius-pill'] = `${t.radius.pill}px`;

  vars['--cf-font'] = t.typography.fontFamily;
  vars['--cf-font-mono'] = t.typography.monoFamily;
  vars['--cf-font-size'] = `${t.typography.baseSize}px`;
  vars['--cf-line-height'] = String(t.typography.lineHeight);

  vars['--cf-nav-bg'] = t.component.nav.background;
  vars['--cf-nav-text'] = t.component.nav.text;
  vars['--cf-nav-active'] = t.component.nav.activeText;
  vars['--cf-nav-border'] = t.component.nav.border;

  vars['--cf-sidebar-bg'] = t.component.sidebar.background;
  vars['--cf-sidebar-text'] = t.component.sidebar.text;
  vars['--cf-sidebar-muted'] = t.component.sidebar.mutedText;
  vars['--cf-sidebar-active-bg'] = t.component.sidebar.activeBackground;
  vars['--cf-sidebar-active-text'] = t.component.sidebar.activeText;
  vars['--cf-sidebar-hover'] = t.component.sidebar.hoverBackground;
  vars['--cf-sidebar-border'] = t.component.sidebar.border;

  vars['--cf-drawer-header-bg'] = t.component.drawer.headerBackground;
  vars['--cf-drawer-header-text'] = t.component.drawer.headerText;
  vars['--cf-drawer-bg'] = t.component.drawer.background;
  vars['--cf-drawer-footer-bg'] = t.component.drawer.footerBackground;

  vars['--cf-table-header-bg'] = t.component.table.headerBackground;
  vars['--cf-table-header-text'] = t.component.table.headerText;
  vars['--cf-table-row'] = t.component.table.rowBackground;
  vars['--cf-table-row-alt'] = t.component.table.rowAltBackground;
  vars['--cf-table-row-hover'] = t.component.table.rowHover;
  vars['--cf-table-row-selected'] = t.component.table.rowSelected;
  vars['--cf-table-border'] = t.component.table.border;

  vars['--cf-input-bg'] = t.component.input.background;
  vars['--cf-input-text'] = t.component.input.text;
  vars['--cf-input-placeholder'] = t.component.input.placeholder;
  vars['--cf-input-border'] = t.component.input.border;
  vars['--cf-input-hover-border'] = t.component.input.hoverBorder;
  vars['--cf-input-focus-border'] = t.component.input.focusBorder;
  vars['--cf-input-error-border'] = t.component.input.errorBorder;

  vars['--cf-btn-bg'] = t.component.button.primaryBackground;
  vars['--cf-btn-text'] = t.component.button.primaryText;
  vars['--cf-btn-hover'] = t.component.button.primaryHover;
  vars['--cf-btn-radius'] = `${t.component.button.radius}px`;

  return vars;
};

/** CSS custom properties for a preset in one mode. */
export const cssVarsForPreset = (
  presetId: string,
  mode: 'light' | 'dark',
): Record<string, string> => tokensToCssVars(getPreset(presetId)[mode]);
